import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  Typography
} from '@mui/material';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import PhoneInput from 'src/components/PhoneInput';
import { useDispatch, useSelector } from 'react-redux';
import theme from 'src/theme';
import {
  VALIDATE_CLIENT_CONTACT_INFO,
  VALIDATE_CLIENT_CURP,
  validateClientContactInformation,
  validateClientCurp
} from 'src/store/actions/bureauFormActions';
import { LoadingButton } from '@mui/lab';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { PersonType } from 'src/utils/constants/clients';
import DropdownMenu from 'src/components/DropdownMenu';
import { loadDenominationsList } from 'src/store/actions/catalogActions';
import Link from '@mui/material/Link';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const PersonTypes = [
  {
    key: PersonType.PHYSICAL,
    name: 'Física'
  },
  {
    key: PersonType.LEGAL,
    name: 'Moral'
  }
];

const StepTwo = ({ data, handleNext, handleBack }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [denominations, setDenominations] = useState([]);
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const loadingValidation = useSelector(
    isLoadingSelector([VALIDATE_CLIENT_CONTACT_INFO.action])
  );

  const loadingValidationCurp = useSelector(
    isLoadingSelector([VALIDATE_CLIENT_CURP.action])
  );
  const { t } = useTranslation();

  const onNext = useCallback(
    async values => {
      if (process.env.REACT_APP_NODE_ENV === 'production') {
        if (values.personType === PersonType.PHYSICAL) {
          dispatch(validateClientCurp(values, null));
        }

        return dispatch(validateClientContactInformation(values, handleNext));
      }
      return handleNext(values);
    },
    [handleNext, dispatch]
  );

  const denominationOptions = useMemo(() => {
    return denominations.map(denomination => ({
      key: denomination.name,
      name: denomination.name
    }));
  }, [denominations]);

  useEffect(() => {
    dispatch(
      loadDenominationsList(list => {
        setDenominations(list);
      })
    );
  }, []);

  return (
    <Box
      sx={{
        height: {
          xs: 'auto',
          md: '50vh'
        },
        justifyContent: 'space-around',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          height: '20%',
          overflow: 'auto'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            marginBottom: '10px',
            color: theme.palette.primary.main
          }}
        >
          {t('BUREAU_FORM.STEP_TWO.TITLE')}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            marginBottom: '10px'
          }}
        >
          {t('BUREAU_FORM.STEP_TWO.DESCRIPTION')}
        </Typography>
      </Box>
      <Formik
        initialValues={initialState}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onNext}
      >
        {({
          errors,
          handleSubmit,
          touched,
          values,
          handleChange,
          handleBlur,
          setFieldValue
        }) => {
          return (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '80%',
                marginTop: '10px'
              }}
            >
              <Box className={classes.container}>
                <FormControl fullWidth margin="normal">
                  <PhoneInput
                    id="phone-label"
                    name="phone"
                    label={t('COMMON.MOBILE_PHONE')}
                    value={values.phone}
                    error={Boolean(touched.phone && errors.phone)}
                    onBlur={handleBlur}
                    onChange={setFieldValue}
                  />
                  {Boolean(touched.phone && errors.phone) && (
                    <FormHelperText sx={{ color: '#d32f2f' }}>
                      {t(errors.phone)}
                    </FormHelperText>
                  )}
                </FormControl>

                <TextField
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && t(errors.email)}
                  label={t('COMMON.EMAIL')}
                  margin="normal"
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.email}
                  variant="outlined"
                />
                <DropdownMenu
                  list={PersonTypes}
                  value={values.personType}
                  setValue={value => setFieldValue('personType', value)}
                  label={t('COMMON.PERSON_TYPE')}
                  error={Boolean(touched.personType && errors.personType)}
                  errorText={touched.personType && t(errors.personType)}
                />

                {values.personType === PersonType.PHYSICAL && (
                  <>
                    <Typography
                      variant="body1"
                      sx={{
                        marginTop: '5px'
                      }}
                    >
                      {t('BUREAU_FORM.STEP_THREE.FIRST_DESCRIPTION')}
                    </Typography>
                    <TextField
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      error={Boolean(touched.rfc && errors.rfc)}
                      fullWidth
                      helperText={touched.rfc && t(errors.rfc)}
                      label={t('COMMON.RFC')}
                      margin="normal"
                      name="rfc"
                      onBlur={handleBlur}
                      onChange={e => {
                        handleChange(e);
                      }}
                      onInput={e => {
                        e.target.value = e.target.value.toUpperCase();
                      }}
                      type="text"
                      value={values.rfc}
                      variant="outlined"
                    />
                    <TextField
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      error={Boolean(touched.curp && errors.curp)}
                      fullWidth
                      helperText={touched.curp && t(errors.curp)}
                      label="CURP"
                      margin="normal"
                      name="curp"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      value={values.curp}
                      variant="outlined"
                    />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <Link
                        href="https://www.gob.mx/curp/"
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {t('COMMON.UNKNOWN_CURP?')}
                      </Link>
                    </Typography>
                  </>
                )}

                {values.personType === PersonType.LEGAL && (
                  <>
                    <Typography
                      variant="body1"
                      sx={{
                        marginTop: '5px'
                      }}
                    >
                      {t('BUREAU_FORM.STEP_THREE.SECOND_DESCRIPTION')}
                    </Typography>
                    <TextField
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      error={Boolean(touched.rfc && errors.rfc)}
                      fullWidth
                      helperText={touched.rfc && t(errors.rfc)}
                      label={t('COMMON.RFC')}
                      margin="normal"
                      name="rfc"
                      onBlur={handleBlur}
                      onChange={e => {
                        handleChange(e);
                      }}
                      onInput={e => {
                        e.target.value = e.target.value.toUpperCase();
                      }}
                      type="text"
                      value={values.rfc}
                      variant="outlined"
                    />
                  </>
                )}
                {values.personType === PersonType.PHYSICAL && (
                  <Typography
                    variant="body1"
                    className={classes.subtitle}
                    sx={{
                      fontzSize: '10px',
                      color: 'text.secondary'
                    }}
                  >
                    {t('BUREAU_FORM.STEP_FOUR.CURP_DATA_DESCRIPTION')}
                  </Typography>
                )}
                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(touched.name && errors.name)}
                  fullWidth
                  helperText={touched.name && t(errors.name)}
                  label={t('COMMON.NAME')}
                  margin="normal"
                  name="name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  disabled={
                    data.name && values.personType === PersonType.PHYSICAL
                  }
                  type="text"
                  value={values.name}
                  variant="outlined"
                />

                {values.personType === PersonType.PHYSICAL && (
                  <>
                    <TextField
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      error={Boolean(
                        touched.paternalName && errors.paternalName
                      )}
                      fullWidth
                      helperText={
                        touched.paternalName && t(errors.paternalName)
                      }
                      label={t('COMMON.PATERNAL_NAME')}
                      margin="normal"
                      name="paternalName"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={data.paternalName}
                      type="text"
                      value={values.paternalName}
                      variant="outlined"
                    />

                    <TextField
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      error={Boolean(
                        touched.maternalName && errors.maternalName
                      )}
                      fullWidth
                      helperText={
                        touched.maternalName && t(errors.maternalName)
                      }
                      label={t('COMMON.MATERNAL_NAME')}
                      margin="normal"
                      name="maternalName"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={data.maternalName}
                      type="text"
                      value={values.maternalName}
                      variant="outlined"
                    />
                  </>
                )}
                {values.personType === PersonType.LEGAL && (
                  <DropdownMenu
                    list={denominationOptions}
                    value={values.denomination || data.denomination}
                    setValue={value => setFieldValue('denomination', value)}
                    label={t('COMMON.DENOMINATION')}
                    error={Boolean(touched.denomination && errors.denomination)}
                    errorText={touched.denomination && t(errors.denomination)}
                  />
                )}
              </Box>

              <Box
                className={classes.box}
                display="flex"
                justifyContent="space-between"
              >
                <Button
                  className={classes.buttons}
                  onClick={() => handleBack()}
                  color="primary"
                  variant="outlined"
                  disabled={loadingValidation || loadingValidationCurp}
                >
                  {t('COMMON.BACK')}
                </Button>
                <LoadingButton
                  className={clsx(classes.buttons, classes.buttonMain)}
                  type="submit"
                  loadingIndicator={
                    <CircularProgress sx={{ color: '#FFFFFF' }} size={20} />
                  }
                  variant="outlined"
                  loading={loadingValidation || loadingValidationCurp}
                  disabled={loadingValidation || loadingValidationCurp}
                >
                  {t('COMMON.NEXT')}
                </LoadingButton>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};
StepTwo.propTypes = {
  data: PropTypes.object,
  handleNext: PropTypes.func,
  handleBack: PropTypes.func
};

export default StepTwo;
